// src/pages/ComprarTokens.js

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/authContext";
import { motion } from "framer-motion";
import { FaCoins } from "react-icons/fa";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../context/authContext";
import toast from "react-hot-toast";
import { volverAClase } from "../utils/navigation";

export default function ComprarTokens() {
  const { user, comprarTokens, EstadoLogout } = useAuth();

  const [paquetes, setPaquetes] = useState([]);
  const [selectedPaquete, setSelectedPaquete] = useState(null);

  const navigate = useNavigate();
  const paypalRef = useRef();

  // 🔹 Frases motivacionales
  const frases = [
    "Lo que no hagas ahora, no lo harás nunca 🚀",
    "Invertir en ti siempre merece la pena 💪",
    "Cada pedalada cuenta, da el siguiente paso 🔥",
    "Hoy es el día perfecto para mejorar 🕒",
    "El futuro se construye pedaleando ahora 🏆",
  ];

  const frase = frases[Math.floor(Math.random() * frases.length)];

  // 🔹 Cargar paquetes desde Firestore
  useEffect(() => {
    const fetchPaquetes = async () => {
      try {
        const querySnapshot = await getDocs(
          collection(db, "paquetesTokens")
        );

        const data = querySnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((p) => p.activo)
          .sort((a, b) => a.orden - b.orden);

        setPaquetes(data);
      } catch (error) {
        console.error("❌ Error cargando paquetes:", error);
        toast.error("Error al cargar los paquetes de tokens.");
      }
    };

    fetchPaquetes();
  }, []);

  // 🔹 Renderizar PayPal cuando hay un paquete seleccionado
  useEffect(() => {
    if (selectedPaquete) {
      if (!document.querySelector("#paypal-sdk")) {
        const script = document.createElement("script");

        script.id = "paypal-sdk";

        script.src = `https://www.paypal.com/sdk/js?client-id=${
          process.env.REACT_APP_PAYPAL_CLIENT_ID
        }&currency=EUR`;

        script.async = true;

        script.onload = () => renderButton();

        document.body.appendChild(script);
      } else {
        renderButton();
      }
    }

    function renderButton() {
      if (window.paypal && paypalRef.current) {
        // Limpiar el botón anterior antes de crear otro
        paypalRef.current.innerHTML = "";

        window.paypal
          .Buttons({
            createOrder: (data, actions) => {
              return actions.order.create({
                purchase_units: [
                  {
                    description: `${selectedPaquete.tokens} tokens`,
                    amount: {
                      currency_code: "EUR",
                      value: selectedPaquete.precio.replace(" €", ""),
                    },
                  },
                ],
              });
            },

            onApprove: async (data, actions) => {
              try {
                const order = await actions.order.capture();

                await comprarTokens(
                  user.uid,
                  selectedPaquete.tokens,
                  {
                    precio: selectedPaquete.precio,
                    transaccionId: order.id,
                    metodo: "PayPal",
                  }
                );

                // 🔹 Refrescar estado del usuario
                await EstadoLogout();

                toast.success("✅ Compra realizada con éxito.");

                navigate("/comprobacion");
              } catch (err) {
                console.error(
                  "❌ Error procesando la compra:",
                  err
                );

                toast.error(
                  "Error al procesar la compra."
                );
              }
            },

            onError: (err) => {
              console.error("❌ Error en PayPal:", err);

              toast.error(
                "Hubo un problema con PayPal. Intenta de nuevo."
              );
            },
          })
          .render(paypalRef.current);
      }
    }
  }, [
    selectedPaquete,
    user,
    navigate,
    comprarTokens,
    EstadoLogout,
  ]);

  return (
    <div className="w-screen min-h-screen bg-gradient-to-br from-gray-950 via-black to-gray-900 text-white">
      <div className="w-full max-w-md mx-auto px-4 py-6">

        {/* Botón volver superior */}
        <motion.button
          type="button"
          whileTap={{ scale: 0.95 }}
          onClick={() => volverAClase(navigate, user?.uid)}
          className="
            flex
            items-center
            gap-2
            mb-6
            px-4
            py-2
            rounded-full
            bg-white/5
            border
            border-white/10
            text-sm
            text-gray-300
            shadow-md
            transition
            active:bg-white/10
          "
        >
          <span>←</span>
          <span>Volver</span>
        </motion.button>

        {/* Cabecera */}
        <div className="text-center mb-8">
          <div
            className="
              w-16
              h-16
              mx-auto
              mb-4
              flex
              items-center
              justify-center
              rounded-3xl
              bg-gradient-to-br
              from-yellow-300
              to-orange-500
              text-black
              text-3xl
              shadow-xl
              shadow-orange-500/20
            "
          >
            <FaCoins />
          </div>

          <p className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-300 mb-2">
            RPM Coach
          </p>

          <h1 className="text-3xl font-black text-white">
            Recarga tus tokens
          </h1>

          <p className="mt-3 text-sm leading-6 text-gray-400">
            Elige tu paquete y sigue disfrutando de las clases,
            apoyando a los instructores y superando tus límites.
          </p>
        </div>

        {/* Ventajas */}
        <div className="grid grid-cols-3 gap-2 mb-7">
          <div className="rounded-2xl bg-white/5 border border-white/10 px-2 py-3 text-center">
            <p className="text-xl">⚡</p>
            <p className="mt-1 text-[11px] text-gray-400">
              Al instante
            </p>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 px-2 py-3 text-center">
            <p className="text-xl">🔒</p>
            <p className="mt-1 text-[11px] text-gray-400">
              Pago seguro
            </p>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 px-2 py-3 text-center">
            <p className="text-xl">🚴</p>
            <p className="mt-1 text-[11px] text-gray-400">
              Sigue rodando
            </p>
          </div>
        </div>

        {/* Título paquetes */}
        <div className="flex items-end justify-between mb-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-500">
              Elige tu recarga
            </p>

            <h2 className="text-lg font-bold text-white">
              Paquetes disponibles
            </h2>
          </div>

          <span className="text-xs text-gray-500">
            Toca para seleccionar
          </span>
        </div>

        {/* Paquetes */}
        <div className="space-y-3">
          {paquetes.map((p, index) => {
            const seleccionado =
              selectedPaquete?.id === p.id;

            const recomendado =
              index === 1 && paquetes.length > 1;

            return (
              <motion.button
                key={p.id}
                type="button"
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelectedPaquete(p)}
                className={`
                  relative
                  w-full
                  overflow-hidden
                  rounded-3xl
                  border
                  p-5
                  text-left
                  shadow-lg
                  transition-all
                  ${
                    seleccionado
                      ? "border-yellow-400 bg-yellow-400/10 shadow-yellow-500/10"
                      : "border-white/10 bg-white/5 active:bg-white/10"
                  }
                `}
              >
                {/* Etiqueta popular */}
                {recomendado && (
                  <span
                    className="
                      absolute
                      top-0
                      right-0
                      rounded-bl-2xl
                      bg-gradient-to-r
                      from-yellow-300
                      to-orange-400
                      px-3
                      py-1.5
                      text-[10px]
                      font-black
                      uppercase
                      tracking-wider
                      text-black
                    "
                  >
                    Más popular
                  </span>
                )}

                <div className="flex items-center justify-between gap-4">
                  {/* Parte izquierda */}
                  <div className="flex items-center gap-4">
                    <div
                      className={`
                        w-12
                        h-12
                        shrink-0
                        flex
                        items-center
                        justify-center
                        rounded-2xl
                        text-xl
                        transition
                        ${
                          seleccionado
                            ? "bg-yellow-400 text-black"
                            : "bg-white/10 text-yellow-300"
                        }
                      `}
                    >
                      <FaCoins />
                    </div>

                    <div>
                      <h3 className="text-xl font-black text-white">
                        {p.tokens} tokens
                      </h3>

                      <p className="mt-1 text-xs text-gray-400">
                        Para clases, propinas y recompensas
                      </p>

                      {p.descuento && (
                        <span
                          className="
                            inline-flex
                            mt-2
                            px-2.5
                            py-1
                            rounded-full
                            bg-green-500/15
                            border
                            border-green-400/20
                            text-[11px]
                            font-bold
                            text-green-300
                          "
                        >
                          🔥 Con descuento
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Parte derecha */}
                  <div className="text-right shrink-0">
                    <p className="text-xl font-black text-yellow-300">
                      {p.precio}
                    </p>

                    <div
                      className={`
                        w-7
                        h-7
                        ml-auto
                        mt-3
                        flex
                        items-center
                        justify-center
                        rounded-full
                        border
                        font-bold
                        transition
                        ${
                          seleccionado
                            ? "border-yellow-400 bg-yellow-400 text-black"
                            : "border-white/20 text-transparent"
                        }
                      `}
                    >
                      ✓
                    </div>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Mensaje cuando no hay paquetes */}
        {paquetes.length === 0 && (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-6 text-center">
            <p className="text-lg">🪙</p>

            <p className="mt-2 font-semibold text-white">
              No hay paquetes disponibles
            </p>

            <p className="mt-1 text-sm text-gray-400">
              Inténtalo de nuevo más tarde.
            </p>
          </div>
        )}

        {/* Resumen y PayPal */}
        {selectedPaquete && (
          <motion.div
            key={selectedPaquete.id}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="
              mt-6
              rounded-3xl
              border
              border-white/10
              bg-white/[0.07]
              p-5
              shadow-2xl
            "
          >
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-white/10">
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500">
                  Tu selección
                </p>

                <p className="mt-1 font-bold text-white">
                  {selectedPaquete.tokens} tokens
                </p>
              </div>

              <p className="text-2xl font-black text-yellow-300">
                {selectedPaquete.precio}
              </p>
            </div>

            <p className="mb-4 text-sm text-center text-gray-300">
              Completa el pago para recibir tus tokens.
            </p>

            {/* PayPal */}
            <div
              ref={paypalRef}
              className="paypal-buttons-container w-full min-h-[48px]"
            />

            <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-500">
              <span>🔒</span>
              <span>Pago seguro procesado por PayPal</span>
            </div>
          </motion.div>
        )}

        {/* Ayuda visual */}
        {!selectedPaquete && paquetes.length > 0 && (
          <div className="mt-5 rounded-2xl border border-yellow-400/10 bg-yellow-400/5 px-4 py-3 text-center">
            <p className="text-sm text-yellow-100/80">
              👆 Selecciona un paquete para mostrar el botón de pago
            </p>
          </div>
        )}

        {/* Frase motivacional */}
        <p className="mt-8 text-center text-sm italic text-gray-500">
          {frase}
        </p>

        {/* Botón volver inferior */}
        <motion.button
          type="button"
          whileTap={{ scale: 0.95 }}
          onClick={() => volverAClase(navigate, user?.uid)}
          className="
            w-full
            mt-6
            py-4
            rounded-2xl
            bg-white/5
            border
            border-white/10
            text-sm
            font-bold
            text-gray-300
            shadow-md
            transition
            active:bg-white/10
          "
        >
          ← Volver a la app
        </motion.button>
      </div>
    </div>
  );
}